import type { EmbedKind } from "./config-snapshot"

/**
 * The install snippet - `main`'s two-element shape, kept on purpose
 * (`DECIDED` 2026-08-26): an element that names the thing, and the script.
 * The id identifies, it does not authenticate. Appearance is NEVER in the
 * snippet: it would freeze the look at paste time, and the element fetches
 * the active config on every load so a publish propagates without
 * re-pasting.
 */
export const EMBED_ELEMENT: Record<EmbedKind, string> = {
  widget: "usereq-widget",
  group: "usereq-group",
}

export const EMBED_ID_ATTRIBUTE: Record<EmbedKind, string> = {
  widget: "widget-id",
  group: "group-id",
}

/**
 * The previous platform's tag named the AGENT, and pages pasted before this
 * platform still carry it. One agent has at most one live chat surface
 * (2026-09-14), so the runtime honours the attribute as an alias: `GET
 * /api/embed/agent/:agentId` answers that surface's widget payload. The
 * alias is accepted on this platform's `<usereq-widget>` too, where
 * `widget-id` wins when both are present; `snippetFor` keeps writing
 * `widget-id` - the id that names the thing on the page.
 */
export const EMBED_AGENT_ATTRIBUTE = "agent-id"

/**
 * The element the previous runtime defined, and the only one it defined:
 * `<usereq-agent-widget agent-id="…">`. Those pages load `@usereq/widget`
 * unpinned, so whatever `latest` resolves to has to answer to this name or
 * they render nothing. The runtime defines it beside its own tags
 * (`EMBED_ARMS` in `packages/embed/src/element.ts`); the snippet never
 * writes it.
 */
export const EMBED_LEGACY_ELEMENT = "usereq-agent-widget"

/**
 * The production API - what the published runtime calls when the element
 * says nothing (`packages/embed/vite.config.ts` bakes this very value).
 * A deployment on another API (dev, staging) names its own through the
 * element's `api-url`; production's snippet carries no such attribute.
 */
export const EMBED_API_URL = "https://api.usereq.com"

const trimSlash = (url: string) => url.replace(/\/+$/, "")

export function snippetFor(
  kind: EmbedKind,
  id: string,
  scriptUrl: string,
  apiUrl: string = EMBED_API_URL
): string {
  const element = EMBED_ELEMENT[kind]
  const api = trimSlash(apiUrl)
  const attributes = [`${EMBED_ID_ATTRIBUTE[kind]}="${id}"`]
  if (api !== EMBED_API_URL) attributes.push(`api-url="${api}"`)
  return [
    `<${element} ${attributes.join(" ")}></${element}>`,
    `<script src="${scriptUrl}" async></script>`,
  ].join("\n")
}
